import React from 'react';
import {
  type GoogleCalendar,
  type MicrosoftCalendar,
  type MicrosoftConnectionStatus,
  type SubscribedCalendar,
} from '../../services';
import { FamilyMember } from '../../types';
import { GoogleAuthNotice } from './GoogleAuthNotice';
import { googleAuthNotice, googleRowError, isSwitchedOffGoogle } from './calendarHealth';

interface CalendarSettingsProps {
  googleCalendars: GoogleCalendar[];
  microsoftCalendars: MicrosoftCalendar[];
  microsoftStatus: MicrosoftConnectionStatus | null;
  subscribedCalendars: SubscribedCalendar[];
  familyMembers: FamilyMember[];
  isSyncing: boolean;
  isSyncingMicrosoft: boolean;
  // Add calendar form
  showAddCalendar: boolean;
  setShowAddCalendar: (show: boolean) => void;
  newCalendarName: string;
  setNewCalendarName: (value: string) => void;
  newCalendarUrl: string;
  setNewCalendarUrl: (value: string) => void;
  newCalendarMember: number | null;
  setNewCalendarMember: (value: number | null) => void;
  isAdding: boolean;
  // Edit URL
  editingCalendarId: string | null;
  editingUrl: string;
  setEditingUrl: (value: string) => void;
  // Handlers
  onSyncGoogle: () => void;
  onToggleGoogleCalendar: (cal: GoogleCalendar) => void;
  onConnectGoogle: () => void;
  onSyncMicrosoft: () => void;
  onToggleMicrosoftCalendar: (cal: MicrosoftCalendar) => void;
  onConnectMicrosoft: () => void;
  onSyncSubscribed: () => void;
  onAddCalendar: (e: React.FormEvent) => void;
  onAssignMember: (calendar: SubscribedCalendar, memberId: number | null) => void;
  onRemoveCalendar: (calendar: SubscribedCalendar) => void;
  onRetryCalendar: (calendar: SubscribedCalendar) => void;
  onEditUrl: (calendar: SubscribedCalendar) => void;
  onSaveUrl: (calendar: SubscribedCalendar) => void;
  onCancelEditUrl: () => void;
}

export const CalendarSettings: React.FC<CalendarSettingsProps> = ({
  googleCalendars,
  microsoftCalendars,
  microsoftStatus,
  subscribedCalendars,
  familyMembers,
  isSyncing,
  isSyncingMicrosoft,
  showAddCalendar,
  setShowAddCalendar,
  newCalendarName,
  setNewCalendarName,
  newCalendarUrl,
  setNewCalendarUrl,
  newCalendarMember,
  setNewCalendarMember,
  isAdding,
  editingCalendarId,
  editingUrl,
  setEditingUrl,
  onSyncGoogle,
  onToggleGoogleCalendar,
  onConnectGoogle,
  onSyncMicrosoft,
  onToggleMicrosoftCalendar,
  onConnectMicrosoft,
  onSyncSubscribed,
  onAddCalendar,
  onAssignMember,
  onRemoveCalendar,
  onRetryCalendar,
  onEditUrl,
  onSaveUrl,
  onCancelEditUrl,
}) => {
  const nonSchoolCalendars = subscribedCalendars.filter(c => !c.is_school_calendar);
  const googleLostAccess = googleAuthNotice(googleCalendars);

  return (
    <section className="settings-section">
      <h2>Calendars</h2>
      <p className="section-hint">Where VizDay gets your events</p>

      {/* Google Calendar */}
      <div className="calendar-group">
        <div className="calendar-group-header">
          <span>Google Calendar</span>
          {googleCalendars.length > 0 && (
            <button
              type="button"
              onClick={onSyncGoogle}
              disabled={isSyncing}
              className="btn-link"
              aria-label={isSyncing ? 'Syncing Google Calendar' : 'Sync Google Calendar now'}
            >
              {isSyncing ? 'Syncing...' : 'Sync'}
            </button>
          )}
        </div>
        {googleLostAccess !== null && (
          <GoogleAuthNotice message={googleLostAccess} onReconnect={onConnectGoogle} />
        )}
        {googleCalendars.length > 0 ? (
          <div className="calendar-list">
            {googleCalendars.map((cal) => {
              // This calendar's own problem (permission, or gone), in the
              // server's words; the account-level notice speaks for auth.
              const rowError = googleRowError(cal);
              return (
              <React.Fragment key={cal.id}>
              <div className="calendar-item calendar-item-toggleable">
                <label className="calendar-toggle">
                  <input
                    type="checkbox"
                    checked={cal.is_enabled}
                    onChange={() => onToggleGoogleCalendar(cal)}
                    aria-label={`Sync ${cal.name}`}
                  />
                  <span className="toggle-switch-small" />
                </label>
                <span
                  className="calendar-dot"
                  style={{ background: cal.color }}
                />
                <span className={`calendar-name ${!cal.is_enabled ? 'calendar-disabled' : ''}`}>
                  {cal.name}
                  {cal.is_primary && <span className="calendar-primary-badge">Primary</span>}
                </span>
                {isSwitchedOffGoogle(cal) && (
                  <span className="gcal-off-label" data-testid={`gcal-off-${cal.id}`}>Off</span>
                )}
              </div>
              {rowError !== null && (
                <p className="gcal-row-error" data-testid={`gcal-error-${cal.id}`}>{rowError}</p>
              )}
              </React.Fragment>
              );
            })}
          </div>
        ) : (
          <div className="calendar-connect">
            <p className="calendar-empty">Not connected</p>
            <button onClick={onConnectGoogle} className="btn-connect-calendar">
              Connect Google Calendar
            </button>
          </div>
        )}
      </div>

      {/* Microsoft Calendar */}
      <div className="calendar-group">
        <div className="calendar-group-header">
          <span>Microsoft Calendar</span>
          {microsoftStatus?.connected && microsoftCalendars.length > 0 && (
            <button
              type="button"
              onClick={onSyncMicrosoft}
              disabled={isSyncingMicrosoft}
              className="btn-link"
              aria-label={isSyncingMicrosoft ? 'Syncing Microsoft Calendar' : 'Sync Microsoft Calendar now'}
            >
              {isSyncingMicrosoft ? 'Syncing...' : 'Sync'}
            </button>
          )}
        </div>
        {microsoftStatus?.connected ? (
          microsoftCalendars.length > 0 ? (
            <div className="calendar-list">
              {microsoftCalendars.map((cal) => (
                <div key={cal.id} className="calendar-item calendar-item-toggleable">
                  <label className="calendar-toggle">
                    <input
                      type="checkbox"
                      checked={cal.is_enabled}
                      onChange={() => onToggleMicrosoftCalendar(cal)}
                      aria-label={`Sync ${cal.name}`}
                    />
                    <span className="toggle-switch-small" />
                  </label>
                  <span
                    className="calendar-dot"
                    style={{ background: cal.color || 'var(--accent-primary)' }}
                  />
                  <span className={`calendar-name ${!cal.is_enabled ? 'calendar-disabled' : ''}`}>
                    {cal.name}
                    {cal.is_default && <span className="calendar-primary-badge">Default</span>}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="calendar-empty-actions">
              <p className="calendar-empty">Connected but no calendars synced</p>
              <button
                type="button"
                onClick={onSyncMicrosoft}
                className="btn-link"
                aria-label="Sync calendars from Microsoft"
              >
                Sync calendars
              </button>
            </div>
          )
        ) : (
          <div className="calendar-connect">
            <p className="calendar-empty">Not connected</p>
            <button onClick={onConnectMicrosoft} className="btn-connect-calendar">
              Connect Microsoft Account
            </button>
          </div>
        )}
      </div>

      {/* Subscribed Calendars */}
      <div className="calendar-group">
        <div className="calendar-group-header">
          <span>Subscribed calendars</span>
          {nonSchoolCalendars.length > 0 && (
            <button
              type="button"
              onClick={onSyncSubscribed}
              disabled={isSyncing}
              className="btn-link"
              aria-label="Sync all subscribed calendars"
            >
              Sync all
            </button>
          )}
        </div>
        {nonSchoolCalendars.length > 0 ? (
          <div className="calendar-list">
            {nonSchoolCalendars.map((cal) => (
              <div key={cal.id} className="calendar-item calendar-item-with-assignment">
                <span
                  className="calendar-dot"
                  style={{ background: cal.color }}
                />
                <div className="calendar-info">
                  <span className="calendar-name">
                    {cal.name}
                    <span className="calendar-count">{cal.event_count} events</span>
                    {cal.sync_status === 'paused' && (
                      <span className="calendar-paused-badge">Not syncing</span>
                    )}
                  </span>
                  {cal.sync_status === 'paused' && (
                    <span className="calendar-sync-error">
                      {cal.last_error}
                      {' '}
                      <button
                        type="button"
                        className="btn-link"
                        onClick={() => onRetryCalendar(cal)}
                        aria-label={`Retry ${cal.name}`}
                      >
                        Retry
                      </button>
                    </span>
                  )}
                  {editingCalendarId === cal.id ? (
                    <div className="calendar-url-edit">
                      <input
                        type="url"
                        value={editingUrl}
                        onChange={(e) => setEditingUrl(e.target.value)}
                        className="calendar-url-input"
                        placeholder="Calendar URL (ICS)"
                        aria-label={`URL for ${cal.name}`}
                        autoFocus
                      />
                      <div className="calendar-url-actions">
                        <button
                          type="button"
                          onClick={() => onSaveUrl(cal)}
                          className="btn-save-url"
                          aria-label={`Save URL for ${cal.name}`}
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          onClick={onCancelEditUrl}
                          className="btn-cancel-url"
                          aria-label={`Cancel editing the URL for ${cal.name}`}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      className="calendar-url"
                      onClick={() => onEditUrl(cal)}
                      title="Click to edit"
                      aria-label={`Edit URL for ${cal.name}: ${cal.url}`}
                    >
                      {cal.url}
                    </button>
                  )}
                  <select
                    className="calendar-member-select"
                    aria-label={`Family member for ${cal.name}`}
                    value={cal.family_member || ''}
                    onChange={(e) => onAssignMember(cal, e.target.value ? Number(e.target.value) : null)}
                  >
                    <option value="">Assign to family member...</option>
                    {familyMembers.map((member) => (
                      <option key={member.id} value={member.id}>
                        {member.name}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  onClick={() => onRemoveCalendar(cal)}
                  className="btn-remove"
                  title="Remove"
                  aria-label={`Remove ${cal.name}`}
                >
                  &times;
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="calendar-empty">No subscribed calendars</p>
        )}

        {showAddCalendar ? (
          <form onSubmit={onAddCalendar} className="add-calendar-form">
            <input
              type="text"
              value={newCalendarName}
              onChange={(e) => setNewCalendarName(e.target.value)}
              placeholder="Calendar name (e.g., Theo's Soccer)"
              className="input-field"
              aria-label="Calendar name"
            />
            <input
              type="url"
              value={newCalendarUrl}
              onChange={(e) => setNewCalendarUrl(e.target.value)}
              placeholder="Calendar URL (ICS)"
              className="input-field"
              aria-label="Calendar URL"
            />
            <select
              value={newCalendarMember || ''}
              onChange={(e) => setNewCalendarMember(e.target.value ? Number(e.target.value) : null)}
              className="input-field"
              aria-label="Assign to family member"
            >
              <option value="">Assign to family member (optional)</option>
              {familyMembers.map((member) => (
                <option key={member.id} value={member.id}>
                  {member.name}
                </option>
              ))}
            </select>
            <div className="form-buttons">
              <button type="submit" disabled={isAdding} className="btn-primary">
                {isAdding ? 'Adding...' : 'Add'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAddCalendar(false);
                  setNewCalendarMember(null);
                }}
                className="btn-link"
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <button
            type="button"
            onClick={() => setShowAddCalendar(true)}
            className="btn-add-calendar"
          >
            + Add calendar URL
          </button>
        )}
      </div>
    </section>
  );
};

export default CalendarSettings;
